//Proofed
import PeopleIcon from '@mui/icons-material/People'
import FilterListIcon from '@mui/icons-material/FilterList'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import SearchIcon from '@mui/icons-material/Search'
import AssessmentIcon from '@mui/icons-material/Assessment'
import type { HowItWorksStep } from '../components/HowItWorksSection'
import GradingIcon from '@mui/icons-material/Grading';
import { Grading } from '@mui/icons-material'

// Hero right-panel highlights
export const expandSectorsHighlights = [
    'Assign dedicated monitors per sector so nothing gets missed and nothing gets buried',
    'Give each BD lead focused, relevant intelligence without the noise',
    'Increase bid throughput without adding headcount',
    'Maintain consistent bid quality as volume grows',
    'Identify which sectors offer the best growth potential and prioritise accordingly',
]

// Challenge cards  section 2
export const expandSectorsChallenges = [
    {
        heading: "You're growing fast but your monitoring and bid process can't keep up",
        body: "A single Core Pro account shared across a growing team quickly becomes unmanageable. Relevant opportunities get buried in noise, sector leads miss tenders in their area and the BD process fragments under the pressure of growth",
    },
    {
        heading: "Your BD team is buried in alerts and stretched across too many submissions",
        body: "Growth means more opportunities to evaluate, more bids to write and more submissions to manage. Without the right structure and tools, your team is stretched thin and the quality and consistency of your submissions begins to suffer",
    },
    {
        heading: "Bid quality is inconsistent as volume grows and writers work under pressure",
        body: "Scaling bid throughput without scaling headcount puts enormous pressure on writers. When time is short, quality suffers. The result is inconsistent responses, missed criteria and win rates that plateau or decline as volume increases",
    },
]

// Recommended solution stack  section 3
export const expandSectorsSolutionStack = [
    {
        Icon: SearchIcon,
        title: 'CFP Core Pro: Multiple Licences',
        body: 'Assign dedicated licences to each sector lead. Each person gets their own filtered digest, focused on their specific area. No shared noise, no missed opportunities, full market coverage per sector',
        tag: 'Platform',
        link: '/products/core',
        linkLabel: 'Learn about Core Pro',
    },
    {
        Icon: AutoAwesomeIcon,
        title: 'Tender Ninja',
        body: 'Maintain bid quality and speed as volume increases. AI-assisted drafting using your document library scales with your team: more bids, same quality, without burning people out',
        tag: 'Platform Add-on',
        link: '/products/tender-ninja',
        linkLabel: 'Learn about Tender Ninja',
    },
    {
        Icon: AssessmentIcon,
        title: 'Win Consultancy Review',
        body: 'An expert review of your BD strategy across sectors. Identifies where you\'re winning, where you\'re losing and how to allocate resource for maximum return, giving you a strategic view of your growing operation',
        tag: 'Advisory',
        link: '/products/bid-services',
        linkLabel: 'Learn about Bid Services',
    },
]

// Journey steps  section 4
export const expandSectorsSteps: HowItWorksStep[] = [
    {
        num: '1',
        Icon: PeopleIcon,
        title: 'Assign Sector Leads',
        body: 'Give each sector lead their own Core Pro licence and configure their personal alert filters. Each person gets a focused daily digest relevant to their sector',
    },
    {
        num: '2',
        Icon: AutoAwesomeIcon,
        title: 'Maintain Bid Quality',
        body: 'Deploy Tender Ninja across your bid team to keep quality high as volume grows. AI-generated first drafts using your knowledge base, so your writers focus on strategy and evidence',
    },
    {
        num: '3',
        Icon: GradingIcon,
        title: 'Review and Reallocate',
        body: 'Commission a Win Consultancy review to assess performance across sectors. Identify where your BD resource is generating return and where it should be reallocated.',
    },
]

// Outcomes checklist  section 5
export const expandSectorsOutcomes = [
    'Each sector lead gets focused, relevant opportunities. No noise, no missed tenders',
    'Bid volumes increase without proportionally increasing headcount',
    'Bid quality stays consistent even as your team produces more responses',
    'You know which sectors are delivering ROI and which are consuming resource without return',
    'Your BD process is structured, measurable and ready to scale further',
]

// Self-qualification checklist  section 6
export const expandSectorsQualifiers = [
    'You\'re already established in public sector and are expanding into adjacent sectors',
    'You have multiple BD leads or sector specialists who need their own focused intelligence',
    'Your current monitoring is too broad, everyone sees everything and important opportunities get missed',
    'You\'re producing more bids than before but quality is suffering under the volume',
    'You want a strategic view of where to invest your growing BD capacity',
]


// FAQs  section 8
export const expandSectorsFaqs = [
    {
        q: 'How do multiple Core Pro licences work?',
        a: "Each licence is assigned to an individual user who configures their own alert filters, saved searches and sector preferences. Users have separate logins and receive personalised daily digests based on their configuration. Licences are managed centrally by an account administrator, who can add, reassign, or remove licences as the team changes. Volume pricing is available for teams taking three or more licences.",
    },
    {
        q: 'Can different team members have different alert filters?',
        a: "Yes, that's the primary benefit of multiple licences. Each user configures their own filters independently: sector categories, CPV codes, buyer organisations, value thresholds, keywords and geographic scope. A healthcare sector lead and a technology sector lead receive completely different digests, each relevant to their area, with no overlap or noise from the other's market.",
    },
    {
        q: 'Does Tender Ninja work across multiple sectors?',
        a: "Yes. Tender Ninja works from your uploaded document library, including case studies, CVs, policies, previous bid responses and company information. It applies this content intelligently regardless of the sector the bid is in. If your library covers multiple sectors, Tender Ninja draws on the most relevant content for each response. You can also segment your library by sector if you prefer.",
    },
    {
        q: 'What is a Win Consultancy review?',
        a: "A Win Consultancy review is a strategic advisory engagement where our team analyses your BD performance across sectors. We look at win rates by sector, resource allocation, submission quality, qualification decisions and pipeline composition. You receive a written report with recommendations on where to invest BD resource for the best return and where you may be over-investing relative to win probability.",
    },
    {
        q: 'How many licences do we need?',
        a: "As a starting point, one licence per active sector lead is the most effective structure. For smaller teams covering multiple sectors, a single licence with broad filters can work initially, but dedicated licences significantly reduce noise and improve focus as team size grows. Our team will review your structure on the discovery call and recommend the right number for your current stage.",
    },
    {
        q: 'Can we add licences as we grow?',
        a: "Yes, licences can be added at any point during your subscription. We recommend reviewing your licence structure every year as your team and sector coverage evolve.",
    },
]

// Related solution links  section 9
export const expandSectorsRelated = [
    {
        headline: 'Grow into Europe & Overseas',
        body: 'Expanding beyond UK borders? Monitor international procurement markets, adapt your bid content and get expert guidance on your first international submissions',
        link: '/solutions/grow-into-europe-and-overseas',
        tag: 'Expanding Reach',
    },
    {
        headline: 'Improve Efficiency & Win Rates',
        body: 'Want to get more from every bid across your sectors? The full CFP suite gives you better targeting, faster drafting and data-driven strategy to improve win rates',
        link: '/solutions/improve-efficiency-and-win-rates',
        tag: 'Execution Support',
    },
]
